import { FilteredTickerPrice } from "@generated/types";

/**
 * Builds a Map keyed by "baseProduct-counterProduct" for O(1) price lookups.
 */
export function buildPricesMap(
  prices: FilteredTickerPrice[] | null
): Map<string, FilteredTickerPrice> {
  const map = new Map<string, FilteredTickerPrice>();
  prices?.forEach((price) => {
    const key = `${price.baseProduct}-${price.counterProduct}`;
    map.set(key, price);
  });
  return map;
}

/**
 * Looks up a price between two currencies. Tries direct lookup first,
 * then reverse lookup with bid/ask inversion.
 * Returns null if no price pair is found, currencies are the same,
 * or a reverse pair has zero bid/ask (would cause division by zero).
 */
export function getPrice(
  pricesMap: Map<string, FilteredTickerPrice>,
  fromCurrency: string,
  toCurrency: string
): FilteredTickerPrice | null {
  if (fromCurrency === toCurrency) {
    return null;
  }

  // Direct lookup
  const directKey = `${toCurrency}-${fromCurrency}`;
  if (pricesMap.has(directKey)) {
    return pricesMap.get(directKey)!;
  }

  // Reverse lookup — invert bid/ask
  const reverseKey = `${fromCurrency}-${toCurrency}`;
  if (pricesMap.has(reverseKey)) {
    const reversePrice = pricesMap.get(reverseKey)!;

    // Guard against division by zero
    if (reversePrice.bid === 0 || reversePrice.ask === 0) {
      return null;
    }

    return {
      ...reversePrice,
      baseProduct: toCurrency,
      counterProduct: fromCurrency,
      bid: 1 / reversePrice.ask, // When inverting, bid becomes 1/ask
      ask: 1 / reversePrice.bid, // When inverting, ask becomes 1/bid
    };
  }

  return null;
}

/**
 * Converts an amount in a given currency to the base currency.
 * Returns the original amount if already in base currency.
 * Returns 0 if no price is found or the price has a zero bid.
 */
export function convertToBaseCurrency(
  amount: number,
  currency: string,
  baseCurrency: string,
  pricesMap: Map<string, FilteredTickerPrice>
): number {
  if (currency === baseCurrency) {
    return amount;
  }

  const price = getPrice(pricesMap, currency, baseCurrency);
  if (!price || price.bid === 0) {
    return 0;
  }

  return amount / price.bid;
}
